import { TextField } from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import CircleIcon from '@mui/icons-material/Circle';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import { useCallback, useMemo, useState } from 'react';

import { GrippingDimensions } from './LabwareFields/GrippingDimensions';
import {
  getAllLabwareDefinitionsOfType,
  getLabwareURI,
} from '../../../labware';
import type { LabwareVersion } from '../../../labware';
import type { LabwarePropertiesForm, LabwareType } from '../../../types';
import { getColorFromIndex, labwareTypeToDisplayName } from '../../../utils';
import Chip from '../../ChipWFB';
import { ColorPickerPanel } from '../../ColorPickerPanel';
import { Icon } from '../../customIcons/labware';
import { TextFieldWithMaxLength } from '../../TextFieldWithMaxLength';

export type Props = {
  handlePendingChange: (update: Partial<LabwarePropertiesForm>) => void;
  handleSubmitForm: () => void;
  labwareColorIndex: number;
  labwareDefinitionURI: string;
  labwareType: LabwareType;
  nickname: string;
  setLabwareColor: (colorIndex: number) => void;
};

export const BaseLabwareSettings = ({
  handlePendingChange,
  handleSubmitForm,
  labwareColorIndex,
  labwareDefinitionURI,
  labwareType,
  nickname,
  setLabwareColor,
}: Props) => {
  const isDimensionOffsetsEnabled = useFlag('fe.dimension_offsets');
  const useTiprackUpdatedLabels = useFlag('fe.tiprack_hide_sterile_label');
  const labwareVersion: LabwareVersion = useTiprackUpdatedLabels
    ? '1.1'
    : '1.0';
  const labwareDefs = getAllLabwareDefinitionsOfType(
    labwareType,
    labwareVersion
  );

  const [showColorPickerPanel, setShowColorPickerPanel] =
    useState<boolean>(false);

  const handleSelectColor = useCallback(
    (colorIndex: number) => {
      setShowColorPickerPanel(false);
      setLabwareColor(colorIndex);
    },
    [setLabwareColor]
  );

  const labwareHtmlColor = getColorFromIndex(labwareColorIndex);

  const labwareOptions = useMemo(
    () =>
      labwareDefs.map((def) => ({
        label: def.displayName,
        value: getLabwareURI(def),
      })),
    [labwareDefs]
  );

  const labwareValue = useMemo(
    () =>
      labwareOptions.find((option) => option.value === labwareDefinitionURI),
    [labwareOptions, labwareDefinitionURI]
  );

  return (
    <Stack gap={1}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap="4rem"
      >
        <Icon
          iconType={labwareType}
          fontSize="large"
          sx={{ color: 'grey.800' }}
        />
        <Chip label={labwareTypeToDisplayName(labwareType)} />
        <CircleIcon
          htmlColor={labwareHtmlColor}
          onClick={() => setShowColorPickerPanel(true)}
          fontSize="large"
        />
        {showColorPickerPanel ? (
          <ColorPickerPanel
            selectedColorIndex={labwareColorIndex}
            handleSelectColor={handleSelectColor}
            handleClickOutside={() => setShowColorPickerPanel(false)}
          />
        ) : null}
      </Stack>

      <FormControl>
        <Autocomplete
          options={labwareOptions}
          value={labwareValue || null}
          onChange={(_, option) => {
            handlePendingChange({ labwareDefinitionURI: option?.value || '' });
          }}
          onBlur={handleSubmitForm}
          renderInput={(inputProps) => (
            <TextField
              {...inputProps}
              variant="outlined"
              label="Labware"
              placeholder="Select labware"
            />
          )}
        />
      </FormControl>

      <TextFieldWithMaxLength
        label="Name"
        value={nickname}
        onChange={(e) => handlePendingChange({ nickname: e.target.value })}
        maxLength={25}
      />

      {labwareValue && isDimensionOffsetsEnabled && (
        <GrippingDimensions labwareDefinitionURI={labwareDefinitionURI} />
      )}
    </Stack>
  );
};
